import {
  ChevronDown,
  Download,
  Loader2,
  MoreVertical,
  Plus,
  Trash,
  Upload,
  X,
} from "lucide-react";
import { Doc, Id } from "@cvx/_generated/dataModel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  VideoProject,
  VideoVersion,
  PlannedPlatform,
  type ConnectedAccount,
} from "@/types/types";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CommentSidebar } from "./CommentSidebar";
import { ConvexError } from "convex/values";
import { PopupCommentForm } from "./CommentPopUpForm";
import StatusBadge from "@/components/Generic/StatusBadge";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { VideoPlayer } from "./VideoPlayer";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useAccount } from "@/state/useAccount";
import { useConvexMutation } from "@convex-dev/react-query";
import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

export const VideoFeedbackDetail = ({
  project,
  allLinkedVideos,
  onBack,
  refetch,
  initialVersionId,
  onNavigate,
}: {
  project: VideoProject;
  allLinkedVideos: Id<"contractVideos">[] | undefined;
  onBack: () => void;
  refetch: () => void;
  initialVersionId?: string;
  onNavigate?: (projectId?: string, versionId?: string) => void;
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [selectedRange, setSelectedRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [compareVersion, setCompareVersion] = useState<VideoVersion | null>(
    null,
  );
  const [compareCurrentTime, setCompareCurrentTime] = useState(0);
  const [compareVideoDuration, setCompareVideoDuration] = useState<
    number | null
  >(null);
  const [activeDrawing, setActiveDrawing] = useState<string | null>(null);
  const compareVideoPlayerSeekRef = useRef<(time: number) => void>();
  const videoFrameCaptureRef =
    useRef<(targetTimestamp?: number) => string | null>();
  const { creatorId } = useAccount();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentFormData, setCommentFormData] = useState<{
    time: number;
    position: { x: number; y: number };
    range?: { start: number; end: number };
    videoFrame?: string;
  } | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    videoFile: null as File | null,
  });
  const [isUploadingVersion, setIsUploadingVersion] = useState(false);
  const videoPlayerSeekRef = useRef<(time: number) => void>();
  const [isDownloadingVersion, setIsDownloadingVersion] = useState(false);
  const [showPlatformSelectionDialog, setShowPlatformSelectionDialog] =
    useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<PlannedPlatform[]>(
    [],
  );
  const [manualPlatformInput, setManualPlatformInput] =
    useState<PlannedPlatform>({ platform: "youtube", handle: "" });
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);
  const [previousVersionCount, setPreviousVersionCount] = useState(
    project.versions.length,
  );
  const [versionToAutoSubmit, setVersionToAutoSubmit] =
    useState<Id<"creatorVideoVersion"> | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    status:
    | "draft"
    | "approved"
    | "rejected"
    | "brand_reviewing"
    | "team_rejected"
    | "needs_review";
  } | null>(null);

  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));

  // Determine user role based on creatorId
  const isCreator = !!creatorId;
  const isFounder = !isCreator;

  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);

  const { mutateAsync: uploadNewVersionMutation } = useMutation({
    mutationFn: useConvexMutation(api.creator_internal_videos.uploadNewVersion),
  });

  const { mutateAsync: addComment } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.addVersionComment,
    ),
  });

  const { mutateAsync: updateVersionStatus } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.updateVersionStatus,
    ),
  });

  const { mutateAsync: deleteComment } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.deleteVersionComment,
    ),
  });

  const { mutateAsync: editComment } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.editVersionComment,
    ),
  });

  const { mutateAsync: deleteProject } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.deleteCreatorVideoProject,
    ),
  });

  const { mutateAsync: deleteVersion } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.deleteCreatorVideoVersion,
    ),
  });

  const { mutateAsync: linkVideoToPublished } = useMutation({
    mutationFn: useConvexMutation(api.videos.linkVideoProjectToContractVideo),
  });

  const { mutateAsync: updatePlannedPlatforms } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.updateProjectPlannedPlatforms,
    ),
  });

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      try {
        const uploadResponse = uploaded[0].response as {
          storageId: Id<"_storage">;
        };

        toast({
          title: "Uploading new version...",
          description:
            "You will be redirected to the projects page after the upload is complete...",
        });

        await uploadNewVersionMutation({
          projectId: project.projectId,
          accountType,
          videoStorageId: uploadResponse.storageId,
        });

        // Set flag to auto-submit after refetch updates the project
        setShouldAutoSubmit(true);
        setPreviousVersionCount(project.versions.length);

        // Refetch to get the updated project with the new version
        refetch();

        toast({
          title: "New version uploaded",
          description: "Submitting for review...",
        });

        setShowUploadForm(false);
        setUploadFormData({ videoFile: null });
      } catch (error) {
        console.error("Error uploading new version:", error);
        toast({
          title: "Error uploading version",
          description: "There was an error uploading your new version",
          variant: "destructive",
        });
      } finally {
        setIsUploadingVersion(false);
      }
    },
    onUploadError: () => {
      toast({
        title: "Error uploading video",
        description: "There was an error uploading your video",
        variant: "destructive",
      });
      setIsUploadingVersion(false);
    },
  });

  const { data: appFounders } = useQuery(
    convexQuery(api.creator_internal_videos.getAppFoundersByProjectId, {
      projectId: project.projectId,
    }),
  );

  // Initialize activeVersion - check for initialVersionId first, then default to latest
  const [activeVersion, setActiveVersion] = useState<VideoVersion>(() => {
    if (initialVersionId) {
      const foundVersion = project.versions.find(
        (v) => v.id === initialVersionId,
      );
      if (foundVersion) {
        return foundVersion;
      }
    }
    return project.versions[project.versions.length - 1];
  });

  // Update activeVersion when initialVersionId changes
  useEffect(() => {
    if (initialVersionId) {
      const foundVersion = project.versions.find(
        (v) => v.id === initialVersionId,
      );
      if (foundVersion && foundVersion.id !== activeVersion.id) {
        setActiveVersion(foundVersion);
      }
    }
  }, [initialVersionId, project.versions, activeVersion.id]);

  // Update activeVersion when project changes (after refetch)
  useEffect(() => {
    // Find the corresponding version in the updated project data
    const updatedVersion = project.versions.find(
      (v) => v.id === activeVersion.id,
    );
    if (updatedVersion) {
      setActiveVersion(updatedVersion);
    } else {
      // If the current version no longer exists, default to the latest
      setActiveVersion(project.versions[project.versions.length - 1]);
    }
  }, [project, activeVersion.id]);

  // Initialize selected platforms from project data
  useEffect(() => {
    if (project.plannedPlatforms) {
      setSelectedPlatforms(project.plannedPlatforms);
    }
  }, [project]);

  // Reset video duration when version changes
  useEffect(() => {
    setVideoDuration(null);
    setCurrentTime(0);
  }, [activeVersion.id]);

  // Determine account type based on user role and video status
  // For creators, always use "creator"
  // For founders/team, use "team" if user is a team member AND status is team_reviewing, otherwise "brand"
  const accountType: "creator" | "team" | "brand" = useMemo(() => {
    if (isCreator) return "creator";

    if (!user || !activeVersion) return "brand";

    const isTeamMember = TEAM_USER_IDS.includes(user._id);
    const isTeamReviewStage = activeVersion.status.includes("team_");

    // Use "team" account type if user is a team member AND status is team_reviewing
    if (isTeamMember && isTeamReviewStage) {
      return "team";
    }

    return "brand";
  }, [isCreator, user, activeVersion?.status]);

  // Query for connected accounts (only for creators)
  const { data: connectedAccounts } = useQuery({
    ...convexQuery(api.creator_internal_videos.getCreatorConnectedAccounts, {
      accountType,
    }),
    enabled: isCreator,
  });

  // Handle version selection and URL update
  const handleVersionSelect = (version: VideoVersion) => {
    setActiveVersion(version);
    // Update URL with new version
    onNavigate?.(project.projectId, version.id);
  };

  const { data: commentsResults, isPending: commentsLoading } = useQuery(
    convexQuery(api.creator_internal_videos.getVersionComments, {
      projectId: project.projectId,
      accountType,
      versionId: activeVersion.id,
    }),
  );

  // Filter comments based on user role and teamVideoReviewRequired
  const filteredComments = useMemo(() => {
    if (!commentsResults?.comments || !user) return commentsResults?.comments;

    const isTeamMember = TEAM_USER_IDS.includes(user._id);

    // Team members always see everything
    if (isTeamMember) {
      return commentsResults.comments;
    }

    // For brand accounts, check if teamVideoReviewRequired is enabled
    // This filtering is now handled on the backend, so we just return the comments
    return commentsResults.comments;
  }, [commentsResults?.comments, user]);

  // Add query for compare version comments
  const { data: compareCommentsResults, isPending: compareCommentsLoading } =
    useQuery({
      ...convexQuery(api.creator_internal_videos.getVersionComments, {
        projectId: project.projectId,
        accountType,
        versionId: compareVersion?.id as Id<"creatorVideoVersion">,
      }),
      enabled: isCompareMode && !!compareVersion,
    });

  // Filter compare comments based on user role and teamVideoReviewRequired
  const filteredCompareComments = useMemo(() => {
    if (!compareCommentsResults?.comments || !user)
      return compareCommentsResults?.comments;

    const isTeamMember = TEAM_USER_IDS.includes(user._id);

    // Team members always see everything
    if (isTeamMember) {
      return compareCommentsResults.comments;
    }

    // For brand accounts, check if teamVideoReviewRequired is enabled
    // This filtering is now handled on the backend, so we just return the comments
    return compareCommentsResults.comments;
  }, [compareCommentsResults?.comments, user]);

  // Query for published videos when version is approved
  const { data: publishedVideos, isLoading: publishedVideosLoading } = useQuery(
    {
      ...convexQuery(api.videos.getCreatorPublishedVideosByContract, {
        contractId: project.contractId,
      }),
      enabled: activeVersion?.status === "approved",
    },
  );

  // Show success message after upload when new version appears
  // Note: Videos now automatically go to review status when uploaded, no manual submission needed
  useEffect(() => {
    if (
      shouldAutoSubmit &&
      project.versions.length > previousVersionCount &&
      isCreator
    ) {
      // Video is automatically submitted for review when uploaded
      // Just show success message and reset state
      toast({
        title: "Version uploaded and submitted",
        description:
          "Your new version has been uploaded and automatically submitted for review",
      });
      setShouldAutoSubmit(false);
      setPreviousVersionCount(project.versions.length);
    }
  }, [
    shouldAutoSubmit,
    project.versions.length,
    previousVersionCount,
    isCreator,
    toast,
  ]);

  // Helper function to toggle compare mode
  const handleToggleCompareMode = () => {
    if (isCompareMode) {
      setIsCompareMode(false);
      setCompareVersion(null);
      setCompareCurrentTime(0);
      setCompareVideoDuration(null);
    } else {
      setIsCompareMode(true);
      // Set default compare version to the first available version that's not the active one
      const availableVersions = project.versions.filter(
        (v) => v.id !== activeVersion.id,
      );
      if (availableVersions.length > 0) {
        setCompareVersion(availableVersions[0]);
      }
    }
  };

  const handleCompareVersionSelect = (version: VideoVersion) => {
    setCompareVersion(version);
    setCompareCurrentTime(0);
    setCompareVideoDuration(null);
  };

  const handleShowCommentForm = (
    time: number,
    position: { x: number; y: number },
    range?: { start: number; end: number },
  ) => {

    let capturedFrame: string | undefined;

    if (!videoFrameCaptureRef.current) {
    } else {
      // Capture the frame at the comment timestamp, not the current video time
      const targetTime = range ? range.start : time;
      capturedFrame = videoFrameCaptureRef.current(targetTime) || undefined;
    }

    // Fallback: provide basic video info even if capture failed
    if (!capturedFrame) {
      const targetTime = range ? range.start : time;
      const fallbackVideoInfo = {
        src: activeVersion.videoUrl,
        currentTime: targetTime, // Use the comment timestamp
        videoWidth: 640,
        videoHeight: 360,
        aspectRatio: 16 / 9,
      };
      capturedFrame = JSON.stringify(fallbackVideoInfo);
    }

    setCommentFormData({
      time,
      position,
      range,
      videoFrame: capturedFrame,
    });
    setShowCommentForm(true);
  };

  const handleAddComment = async (
    content: string,
    attachments?: Array<{
      storageId: Id<"_storage">;
      name: string;
      type: string;
      size: number;
    }>,
    drawing?: string,
  ) => {
    if (!commentFormData) return;

    try {
      await addComment({
        versionId: activeVersion.id,
        accountType,
        message: content,
        timestampRange: {
          start: commentFormData.time ?? commentFormData.range?.start,
          end: commentFormData.range?.end,
        },
        attachments,
        drawing,
      });

      toast({
        title: "Comment added",
        description: "Your comment has been added",
      });
    } catch (error) {
      console.error("Error adding comment:", error);
      toast({
        title: "Error adding comment",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error adding your comment",
        variant: "destructive",
      });
    }
    setShowCommentForm(false);
    setSelectedRange(null);
    setCommentFormData(null);
  };

  const handleCancelComment = () => {
    setShowCommentForm(false);
    setSelectedRange(null);
    setCommentFormData(null);
  };

  const handleReply = async (
    commentId: Id<"creatorVideoComments">,
    content: string,
    attachments?: Array<{
      storageId: Id<"_storage">;
      name: string;
      type: string;
      size: number;
    }>,
  ) => {
    try {
      await addComment({
        versionId: activeVersion.id,
        accountType,
        message: content,
        replyTo: commentId,
        timestampRange: {
          start: 0,
          end: 0,
        },
        attachments,
      });
      toast({
        title: "Reply added",
        description: "Your reply has been added",
      });
    } catch (error) {
      console.error("Error adding reply:", error);
      toast({
        title: "Error adding reply",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error adding your reply",
        variant: "destructive",
      });
    }
  };

  const handleSeekToComment = (time: number) => {
    setCurrentTime(time);
    // Use the exposed seekTo function from VideoPlayer
    if (videoPlayerSeekRef.current) {
      videoPlayerSeekRef.current(time);
    }
  };

  const handleStatusChangeClick = (
    status:
      | "draft"
      | "approved"
      | "rejected"
      | "brand_reviewing"
      | "team_rejected"
      | "needs_review",
  ) => {
    // Show confirmation for Approve and Reject, but not for Needs Review
    if (
      status === "approved" ||
      status === "rejected" ||
      status === "team_rejected" ||
      status === "brand_reviewing"
    ) {
      setPendingStatusChange({ status });
      setShowConfirmDialog(true);
    } else {
      // Needs Review doesn't need confirmation
      handleStatusChange(status);
    }
  };

  const handleStatusChange = async (
    status:
      | "draft"
      | "approved"
      | "rejected"
      | "brand_reviewing"
      | "team_rejected"
      | "needs_review",
  ) => {
    if (!activeVersion) return;

    try {
      await updateVersionStatus({
        projectId: project.projectId,
        versionId: activeVersion.id,
        status: status,
        accountType,
      });

      refetch();
      toast({
        title: "Version status updated",
        description: "Your version status has been updated",
      });
    } catch (error) {
      console.error("Error updating version status:", error);
      toast({
        title: "Error updating version status",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error updating your version status",
        variant: "destructive",
      });
    }
  };

  const handleConfirmStatusChange = () => {
    if (pendingStatusChange) {
      handleStatusChange(pendingStatusChange.status);
      setShowConfirmDialog(false);
      setPendingStatusChange(null);
    }
  };

  const handleSavePlannedPlatforms = async () => {
    try {
      await updatePlannedPlatforms({
        projectId: project.projectId,
        accountType,
        plannedPlatforms: selectedPlatforms,
      });

      toast({
        title: "Platforms saved",
        description: "Your planned platforms have been saved",
      });

      setShowPlatformSelectionDialog(false);
      if (versionToAutoSubmit) {
        setVersionToAutoSubmit(null);
      }

      refetch();
    } catch (error) {
      console.error("Error saving platforms:", error);
      toast({
        title: "Error",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error saving your platforms",
        variant: "destructive",
      });
    }
  };

  const handleAddManualPlatform = () => {
    if (!manualPlatformInput.handle.trim()) {
      toast({
        title: "Handle required",
        description: "Please enter a handle",
        variant: "destructive",
      });
      return;
    }

    // Check if this platform/handle combo already exists
    const exists = selectedPlatforms.some(
      (p) =>
        p.platform === manualPlatformInput.platform &&
        p.handle === manualPlatformInput.handle,
    );

    if (exists) {
      toast({
        title: "Platform already added",
        description:
          "This platform and handle combination is already in your list",
        variant: "destructive",
      });
      return;
    }

    setSelectedPlatforms([...selectedPlatforms, manualPlatformInput]);
    setManualPlatformInput({ platform: "youtube", handle: "" });
  };

  const handleRemovePlatform = (index: number) => {
    setSelectedPlatforms(selectedPlatforms.filter((_, i) => i !== index));
  };

  const handleAddConnectedAccount = (
    platform: PlannedPlatform["platform"],
    handle: string,
  ) => {
    // Check if already added
    const exists = selectedPlatforms.some(
      (p) => p.platform === platform && p.handle === handle,
    );

    if (exists) {
      toast({
        title: "Already added",
        description: "This account is already in your list",
        variant: "destructive",
      });
      return;
    }

    setSelectedPlatforms([...selectedPlatforms, { platform, handle }]);
  };

  const handleVideoDurationLoaded = (duration: number) => {
    setVideoDuration(duration);
  };

  const handleDeleteComment = async (commentId: Id<"creatorVideoComments">) => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      try {
        await deleteComment({
          commentId,
          accountType,
        });
        toast({
          title: "Comment deleted",
          description: "Your comment has been deleted",
        });
      } catch (error) {
        console.error("Error deleting comment:", error);
        toast({
          title: "Error deleting comment",
          description:
            error instanceof ConvexError
              ? error.data
              : "There was an error deleting your comment",
          variant: "destructive",
        });
      }
    }
  };

  const handleEditComment = async (
    commentId: Id<"creatorVideoComments">,
    content: string,
  ) => {
    try {
      await editComment({
        commentId,
        message: content,
        accountType,
      });
      toast({
        title: "Comment updated",
        description: "Your comment has been updated",
      });
    } catch (error) {
      console.error("Error editing comment:", error);
      toast({
        title: "Error editing comment",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error editing your comment",
        variant: "destructive",
      });
    }
  };

  const handleDeleteProject = async () => {
    if (
      window.confirm(
        `Are you sure you want to delete the project "${project.title}"? This action cannot be undone and will delete all versions and comments.`,
      )
    ) {
      try {
        await deleteProject({
          projectId: project.projectId,
          accountType,
        });

        toast({
          title: "Project deleted",
          description: "The project has been deleted successfully",
        });

        // Navigate back to projects list
        onBack();
      } catch (error) {
        console.error("Error deleting project:", error);
        toast({
          title: "Error deleting project",
          description:
            error instanceof ConvexError
              ? error.data
              : "There was an error deleting the project",
          variant: "destructive",
        });
      }
    }
  };

  const handleDeleteVersion = async (versionId: string, versionNumber: number) => {
    if (
      window.confirm(
        `Are you sure you want to delete version ${versionNumber}? This action cannot be undone and will delete all comments for this version.`,
      )
    ) {
      try {
        await deleteVersion({
          versionId: versionId as Id<"creatorVideoVersion">,
          accountType,
        });

        toast({
          title: "Version deleted",
          description: `Version ${versionNumber} has been deleted successfully`,
        });

        // If we deleted the active version, switch to another version
        if (activeVersion.id === versionId) {
          const remainingVersions = project.versions.filter(
            (v) => v.id !== versionId,
          );
          if (remainingVersions.length > 0) {
            handleVersionSelect(remainingVersions[remainingVersions.length - 1]);
          }
        }

        // Refetch to get updated project data
        refetch();
      } catch (error) {
        console.error("Error deleting version:", error);
        toast({
          title: "Error deleting version",
          description:
            error instanceof ConvexError
              ? error.data
              : "There was an error deleting the version",
          variant: "destructive",
        });
      }
    }
  };

  const handleDownloadActiveVersion = async () => {
    if (!activeVersion?.videoUrl || isDownloadingVersion) return;

    setIsDownloadingVersion(true);

    try {
      // Create download link directly from video URL
      const versionNumber =
        project.versions.findIndex((v) => v.id === activeVersion.id) + 1;
      const link = document.createElement("a");
      link.href = activeVersion.videoUrl;
      link.download = `${project.title}_v${versionNumber}.mp4`;
      link.target = "_blank"; // Open in new tab as fallback
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading video:", error);
      toast({
        title: "Error downloading video",
        description: "There was an error downloading the video",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingVersion(false);
    }
  };

  const handleLinkToPublishedVideo = async (
    contractVideoId: Id<"contractVideos">,
  ) => {
    try {
      await linkVideoToPublished({
        projectId: project.projectId,
        contractVideoId: contractVideoId,
      });

      toast({
        title: "Video linked successfully",
        description: "The project has been linked to the published video",
      });

      refetch();
    } catch (error) {
      console.error("Error linking video:", error);
      toast({
        title: "Error linking video",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error linking the video",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6 mb-48">
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Status Change</DialogTitle>
            <DialogDescription>
              {pendingStatusChange?.status === "approved" ||
                pendingStatusChange?.status === "brand_reviewing"
                ? "Are you sure you want to approve this video? This will notify the creator/brand."
                : "Are you sure you want to reject this video? This will notify the creator/brand."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowConfirmDialog(false);
                setPendingStatusChange(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmStatusChange}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Popup Comment Form */}
      {showCommentForm && commentFormData && (
        <PopupCommentForm
          position={commentFormData.position}
          timestamp={commentFormData.time}
          selectedRange={commentFormData.range}
          onSubmit={handleAddComment}
          onCancel={handleCancelComment}
          videoFrame={commentFormData.videoFrame}
        />
      )}

      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronDown className="h-4 w-4 mr-2 rotate-90" />
            Back to Projects
          </Button>
          <h2 className="text-2xl font-semibold text-primary">
            {project.title}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadActiveVersion}
            disabled={isDownloadingVersion}
            title={
              isDownloadingVersion
                ? "Downloading..."
                : `Download version ${project.versions.findIndex((v) => v.id === activeVersion.id) + 1}`
            }
          >
            {isDownloadingVersion ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Download
              </>
            )}
          </Button>
          {isCreator &&
            project.versions.some((v) => v.status !== "approved") && (
              <Button onClick={() => setShowUploadForm(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload New Version
              </Button>
            )}
          {isFounder && (
            <Button variant="destructive" onClick={handleDeleteProject}>
              <Trash className="h-4 w-4 mr-2" />
              Delete Project
            </Button>
          )}
        </div>
      </div>

      {/* Upload Form Modal */}
      {showUploadForm && isCreator && (
        <Card className="p-4">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-primary">
              Upload New Version
            </h3>

            <div>
              <label className="text-sm font-medium text-primary">
                Video File
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setUploadFormData((prev) => ({ ...prev, videoFile: file }));
                }}
                className="w-full mt-1 px-3 py-2 border border-border rounded-lg"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (uploadFormData.videoFile) {
                    setIsUploadingVersion(true);
                    startUpload([uploadFormData.videoFile]);
                  }
                }}
                disabled={!uploadFormData.videoFile || isUploadingVersion}
              >
                {isUploadingVersion ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  "Upload Version"
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowUploadForm(false);
                  setUploadFormData({ videoFile: null });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Platform Selection Dialog */}
      {showPlatformSelectionDialog && isCreator && (
        <Card className="p-6 space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-primary mb-2">
              Select Posting Platforms
            </h3>
            <p className="text-sm text-primary/60">
              Choose which social media platforms you plan to post this video on
            </p>
          </div>

          {/* Connected Accounts Section */}
          {connectedAccounts &&
            [
              ...(connectedAccounts.youtube ?? []),
              ...(connectedAccounts.tiktok ?? []),
              ...(connectedAccounts.instagram ?? []),
            ].length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-primary">
                  Your Connected Accounts
                </h4>
                <div className="space-y-2">
                  {connectedAccounts.youtube &&
                    connectedAccounts.youtube.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-primary/60 font-medium">
                          YouTube
                        </p>
                        {connectedAccounts.youtube.map(
                          (account: ConnectedAccount) => (
                            <Button
                              key={account.handle}
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleAddConnectedAccount(
                                  "youtube",
                                  account.handle,
                                )
                              }
                              className="w-full justify-start"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {account.handle}
                              {account.followerCount &&
                                ` (${account.followerCount.toLocaleString()} followers)`}
                            </Button>
                          ),
                        )}
                      </div>
                    )}
                  {connectedAccounts.tiktok &&
                    connectedAccounts.tiktok.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-primary/60 font-medium">
                          TikTok
                        </p>
                        {connectedAccounts.tiktok.map(
                          (account: ConnectedAccount) => (
                            <Button
                              key={account.handle}
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleAddConnectedAccount(
                                  "tiktok",
                                  account.handle,
                                )
                              }
                              className="w-full justify-start"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {account.handle}
                              {account.followerCount &&
                                ` (${account.followerCount.toLocaleString()} followers)`}
                            </Button>
                          ),
                        )}
                      </div>
                    )}
                  {connectedAccounts.instagram &&
                    connectedAccounts.instagram.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-primary/60 font-medium">
                          Instagram
                        </p>
                        {connectedAccounts.instagram.map(
                          (account: ConnectedAccount) => (
                            <Button
                              key={account.handle}
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleAddConnectedAccount(
                                  "instagram",
                                  account.handle,
                                )
                              }
                              className="w-full justify-start"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {account.handle}
                              {account.followerCount &&
                                ` (${account.followerCount.toLocaleString()} followers)`}
                            </Button>
                          ),
                        )}
                      </div>
                    )}
                </div>
              </div>
            )}

          {/* Manual Input Section */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <Select
                value={manualPlatformInput.platform}
                onValueChange={(
                  value: "youtube" | "tiktok" | "instagram" | "youtube-shorts",
                ) =>
                  setManualPlatformInput({
                    ...manualPlatformInput,
                    platform: value,
                  })
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="youtube">YouTube</SelectItem>
                  <SelectItem value="youtube-shorts">YouTube Shorts</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                </SelectContent>
              </Select>
              <input
                type="text"
                placeholder="Enter handle (e.g., @username)"
                value={manualPlatformInput.handle}
                onChange={(e) =>
                  setManualPlatformInput({
                    ...manualPlatformInput,
                    handle: e.target.value,
                  })
                }
                className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
              />
              <Button size="sm" onClick={handleAddManualPlatform}>
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>

          {/* Selected Platforms */}
          {selectedPlatforms.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-primary">
                Selected Platforms ({selectedPlatforms.length})
              </h4>
              <div className="space-y-2">
                {selectedPlatforms.map((platform, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border border-border rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium capitalize">
                        {platform.platform.replace("-", " ")}
                      </p>
                      <p className="text-xs text-primary/60">
                        {platform.handle}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemovePlatform(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleSavePlannedPlatforms}
              disabled={selectedPlatforms.length === 0}
              className="flex-1"
            >
              Save & Submit for Review
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowPlatformSelectionDialog(false);
                setVersionToAutoSubmit(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {/* Project controls */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Version selector */}
            <div className="flex items-center gap-2">
              <div className="flex gap-2">
                {project.versions.map((version, index) => (
                  <Button
                    key={version.id}
                    size="sm"
                    variant={
                      version.id === activeVersion.id ? "default" : "outline"
                    }
                    onClick={() => {
                      handleVersionSelect(version);
                    }}
                  >
                    v{index + 1}
                  </Button>
                ))}
              </div>
              {isFounder && project.versions.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        const versionIndex =
                          project.versions.findIndex(
                            (v) => v.id === activeVersion.id,
                          ) + 1;
                        handleDeleteVersion(activeVersion.id, versionIndex);
                      }}
                    >
                      <Trash className="h-4 w-4 mr-2" />
                      Delete Version
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Compare Versions Button */}
            {project.versions.length > 1 && (
              <Button
                size="sm"
                variant={isCompareMode ? "default" : "outline"}
                onClick={handleToggleCompareMode}
              >
                {isCompareMode ? "Exit Compare" : "Compare Versions"}
              </Button>
            )}

            {/* Compare Version Selector */}
            {isCompareMode && compareVersion && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-primary/60">Compare with:</span>
                <div className="flex gap-2">
                  {project.versions
                    .filter((v) => v.id !== activeVersion.id)
                    .map((version) => {
                      const originalIndex = project.versions.findIndex(
                        (v) => v.id === version.id,
                      );
                      return (
                        <Button
                          key={version.id}
                          size="sm"
                          variant={
                            version.id === compareVersion.id
                              ? "default"
                              : "outline"
                          }
                          onClick={() => handleCompareVersionSelect(version)}
                        >
                          v{originalIndex + 1}
                        </Button>
                      );
                    })}
                </div>
              </div>
            )}
          </div>

          {/* Status Management */}
          {activeVersion && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-primary/60">Status:</span>

              {isFounder ? (
                // Founder sees simplified actions (not raw status dropdown)
                <div className="flex items-center gap-2">
                  <StatusBadge status={activeVersion.status} />
                  {/* Only show action buttons for reviewing statuses */}
                  {(activeVersion.status?.includes("reviewing") ||
                    activeVersion.status === "ai_approved") && (
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          {/* Show approve button for founders - only for brand_reviewing status */}
                          {isFounder &&
                            activeVersion.status === "brand_reviewing" && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleStatusChangeClick("approved")}
                              >
                                Approve
                              </Button>
                            )}
                          {/* Show team approve button for team members - sends to brand_reviewing */}
                          {accountType === "team" &&
                            activeVersion.status === "team_reviewing" && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() =>
                                  handleStatusChangeClick("brand_reviewing")
                                }
                              >
                                Approve
                              </Button>
                            )}
                          {/* Needs Review button - replaces old Reject for most cases */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStatusChangeClick("needs_review")}
                          >
                            Needs Review
                          </Button>
                          {/* Reject button - for truly unusable videos */}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() =>
                              handleStatusChangeClick(
                                accountType === "team"
                                  ? "team_rejected"
                                  : "rejected",
                              )
                            }
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    )}
                  {activeVersion.status === "approved" && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-primary/60">
                        Link to published video:
                      </span>
                      <Select
                        value={project.contractVideoId ?? undefined}
                        onValueChange={(value) => {
                          if (value && value !== "placeholder") {
                            handleLinkToPublishedVideo(
                              value as Id<"contractVideos">,
                            );
                          }
                        }}
                        disabled={publishedVideosLoading}
                      >
                        <SelectTrigger className="w-80">
                          {project.contractVideoId ? (
                            <SelectValue>
                              {publishedVideos?.find(
                                (v) => v._id === project.contractVideoId,
                              )?.title ||
                                publishedVideos?.find(
                                  (v) => v._id === project.contractVideoId,
                                )?.url ||
                                "Linked published video"}
                            </SelectValue>
                          ) : (
                            <SelectValue
                              placeholder={
                                publishedVideosLoading
                                  ? "Loading videos..."
                                  : "Select a published video to link"
                              }
                            />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          {publishedVideos && publishedVideos.length > 0 ? (
                            publishedVideos
                              // Show all published videos, but allow selecting the one already linked even if it's in allLinkedVideos
                              .filter(
                                (video) =>
                                  !allLinkedVideos?.includes(video._id) ||
                                  video._id === project.contractVideoId,
                              )
                              .map((video) => (
                                <SelectItem key={video._id} value={video._id}>
                                  {video.title || video.url} -{" "}
                                  {video.views?.toLocaleString() || 0} views
                                </SelectItem>
                              ))
                          ) : (
                            <SelectItem value="placeholder" disabled>
                              No published videos found
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ) : (
                // Creator sees status badge
                <div className="flex items-center gap-3">
                  <StatusBadge status={activeVersion.status} />
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Main video interface - 2-column layout with equal heights */}
      {activeVersion && (
        <div
          className={`grid gap-6 min-w-fit pb-48 ${isCompareMode
            ? "grid-cols-1 lg:grid-cols-3 h-[500px]"
            : "grid-cols-1 lg:grid-cols-2 h-[500px]"
            }`}
        >
          {/* Video Section(s) - Fixed height container */}
          <div
            className={`flex ${isCompareMode ? "lg:col-span-2" : "flex-col"} h-full ${isCompareMode ? "gap-4" : "space-y-4"
              }`}
          >
            {/* Primary Video */}
            <div
              className={`${isCompareMode ? "flex-1" : "w-full"} flex flex-col`}
            >
              {isCompareMode && (
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-primary">
                    Version{" "}
                    {project.versions.findIndex(
                      (v) => v.id === activeVersion.id,
                    ) + 1}
                    {activeVersion.status && (
                      <span className="ml-2">
                        <StatusBadge status={activeVersion.status} />
                      </span>
                    )}
                  </h3>
                </div>
              )}
              <div className="flex-1">
                <VideoPlayer
                  version={activeVersion}
                  videoDuration={videoDuration}
                  comments={filteredComments ?? []}
                  onTimeUpdate={setCurrentTime}
                  currentTime={currentTime}
                  selectedRange={selectedRange}
                  onRangeSelect={setSelectedRange}
                  onShowCommentForm={handleShowCommentForm}
                  onDurationLoaded={handleVideoDurationLoaded}
                  onExposeSeekFunction={(seekFn) => {
                    videoPlayerSeekRef.current = seekFn;
                  }}
                  onExposeVideoFrame={(getFrameFn) => {
                    videoFrameCaptureRef.current = getFrameFn;
                  }}
                  activeDrawing={activeDrawing}
                  onDrawingDisplay={setActiveDrawing}
                />
              </div>
            </div>

            {/* Comparison Video */}
            {isCompareMode && compareVersion && (
              <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-primary">
                    Version{" "}
                    {project.versions.findIndex(
                      (v) => v.id === compareVersion.id,
                    ) + 1}
                    {compareVersion.status && (
                      <span className="ml-2">
                        <StatusBadge status={compareVersion.status} />
                      </span>
                    )}
                  </h3>
                </div>
                <div className="flex-1">
                  <VideoPlayer
                    version={compareVersion}
                    videoDuration={compareVideoDuration}
                    comments={filteredCompareComments ?? []}
                    onTimeUpdate={setCompareCurrentTime}
                    currentTime={compareCurrentTime}
                    selectedRange={null}
                    onRangeSelect={() => { }} // Disable range selection on compare video
                    onShowCommentForm={(time, position) => {
                      // Handle comment form for compare video
                      setCommentFormData({ time, position });
                      setShowCommentForm(true);
                    }}
                    onDurationLoaded={(duration) =>
                      setCompareVideoDuration(duration)
                    }
                    onExposeSeekFunction={(seekFn) => {
                      compareVideoPlayerSeekRef.current = seekFn;
                    }}
                    activeDrawing={null} // Don't show drawings on compare video
                    onDrawingDisplay={() => { }} // Disable drawing display on compare video
                  />
                </div>
              </div>
            )}
          </div>

          {/* Comments Section - Fixed height with scrollable content */}
          <div
            className="h-full overflow-auto min-w-0"
            style={{ minWidth: "400px" }}
          >
            <CommentSidebar
              comments={
                isCompareMode && compareCommentsResults
                  ? [
                    ...(filteredComments ?? []).map((comment) => ({
                      ...comment,
                      _versionInfo: {
                        versionNumber:
                          project.versions.findIndex(
                            (v) => v.id === activeVersion.id,
                          ) + 1,
                        versionId: activeVersion.id,
                      },
                    })),
                    ...(filteredCompareComments ?? []).map((comment) => ({
                      ...comment,
                      _versionInfo: {
                        versionNumber:
                          project.versions.findIndex(
                            (v) => v.id === (compareVersion?.id || ""),
                          ) + 1,
                        versionId: compareVersion?.id || "",
                      },
                    })),
                  ].sort(
                    (a, b) =>
                      (a.timestampRange?.start || 0) -
                      (b.timestampRange?.start || 0),
                  )
                  : (filteredComments ?? [])
              }
              appFounders={appFounders ?? []}
              onReply={handleReply}
              onSeekToComment={(time) => {
                // In compare mode, seek both videos to the same time
                handleSeekToComment(time);
                if (isCompareMode && compareVideoPlayerSeekRef.current) {
                  setCompareCurrentTime(time);
                  compareVideoPlayerSeekRef.current(time);
                }
              }}
              onDeleteComment={handleDeleteComment}
              onEditComment={handleEditComment}
              currentUser={user as Doc<"users"> | undefined}
              isCompareMode={isCompareMode}
              isLoading={
                isCompareMode
                  ? commentsLoading || compareCommentsLoading
                  : commentsLoading
              }
              onDrawingDisplay={setActiveDrawing}
            />
          </div>
        </div>
      )}
    </div>
  );
};
